/**
 * 月締め作業の計算(2026-09-24 発注者指示。`docs/specs/month-end.md`。P-67)。
 * 画面から切り離した純粋な計算だけを置く(Firebase の読み書きは monthCloseRepo・画面が行う)。
 *
 *  ① 報酬の確定: 人(BB ID)ごとに **日ごと計算(いまのシステム)** と **月まとめ計算(月合計の時間×時給)** の両方を出す(D-136②)
 *  ② 歩合: (取得件数 − キャンセル件数) × 件数単価
 *  ③ 通話料: アジャストワンの請求明細CSV(`callCharge.ts` で読む)を 会社×日 に集計
 *  ④ 請求書: 会社ごとに Σ(その日の納品件数 × その日の会社単価)
 */
import { priceOn, type Company, type DateStr, type Delivery, type Yen } from '@core/lib/types';
import { incentiveOf, type IncentiveSettings } from '@core/masters/settingsPayRepo';
import type { StaffMetrics } from './calc';

/** ①の1行 = BB ID(同じ人でも給湯器と不用品は別の行。報酬の計算単位) */
export type MonthPayRow = {
  bb_id: string;
  op_id: string;
  name: string;
  work_style: StaffMetrics['work_style'];
  pay_type: StaffMetrics['pay_type'];
  business: StaffMetrics['business'];
  /** 月末時点で使っていた単価(時給 or 件数単価) */
  wage: Yen;
  /** 稼働した日数 */
  days: number;
  /** 日ごとの実稼働(0.5h 丸めあり)の合計 */
  hours_daily: number;
  /** 丸める前の稼働の合計(出勤・タイミー=拘束−休憩 / 在宅・管理=受付＋通話＋後処理) */
  hours_raw: number;
  /** 取得件数(月合計) */
  appointments: number;
  /**
   * A: 日ごとに計算した報酬の合計(いまのシステム。PL・日次【集計】と同じ値)。
   * **交通費は含まない**(出勤の `pay` には交通費が入っているので引いてある。交通費は別の列)
   */
  pay_daily: Yen;
  /** B: 月まとめで計算した報酬(単価ごとに 丸める前の時間 × 時給。歩合は 取得 × 単価) */
  pay_month: Yen;
  /** 交通費(その月に支給した額の合計) */
  commute: Yen;
};

/** 1人ぶんの月の合計を出すための下ごしらえ */
type Acc = {
  row: MonthPayRow;
  dates: Set<DateStr>;
  /** 単価ごとの「丸める前の時間」。月の途中で時給が変わっても正しく計算するため */
  hoursByWage: Map<number, number>;
  /** 単価ごとの取得件数(歩合用) */
  apptsByWage: Map<number, number>;
  lastDate: DateStr;
};

/** その行の「丸める前の稼働時間」(h)。出勤・タイミー=拘束−休憩、在宅・管理=受付＋通話＋後処理 */
export function rawHoursOf(m: StaffMetrics): number {
  if (m.hours_source === 'auto') return Math.max(0, (m.login_sec - m.break_sec) / 3600);
  // 手で直した日(manual)はその値が正。在宅・管理は BB稼働がそのまま実稼働
  if (m.hours_source === 'manual') return m.worked_hours;
  return (m.reception_sec + m.talk_sec + m.after_sec) / 3600;
}

/** ①報酬の確定: 期間内の全行から、人(BB ID)ごとの月合計を作る */
export function monthPayRows(rows: StaffMetrics[]): MonthPayRow[] {
  const acc = new Map<string, Acc>();
  for (const m of rows) {
    let a = acc.get(m.bb_id);
    if (!a) {
      a = {
        row: {
          bb_id: m.bb_id,
          op_id: m.op_id,
          name: m.name,
          work_style: m.work_style,
          pay_type: m.pay_type,
          business: m.business,
          wage: m.wage,
          days: 0,
          hours_daily: 0,
          hours_raw: 0,
          appointments: 0,
          pay_daily: 0,
          pay_month: 0,
          commute: 0,
        },
        dates: new Set<DateStr>(),
        hoursByWage: new Map<number, number>(),
        apptsByWage: new Map<number, number>(),
        lastDate: m.date,
      };
      acc.set(m.bb_id, a);
    }
    const r = a.row;
    r.hours_daily += m.worked_hours;
    r.appointments += m.appointments;
    // 出勤・タイミーの pay には交通費が入っている(buildDailySummary)。ここでは報酬だけにして、交通費は別に足す
    r.pay_daily += m.pay - m.commute;
    r.commute += m.commute;
    const raw = rawHoursOf(m);
    r.hours_raw += raw;
    if (m.wage > 0) {
      a.hoursByWage.set(m.wage, (a.hoursByWage.get(m.wage) ?? 0) + raw);
      a.apptsByWage.set(m.wage, (a.apptsByWage.get(m.wage) ?? 0) + m.appointments);
    }
    if (m.appointments > 0 || m.worked_hours > 0) a.dates.add(m.date);
    // 単価はその月の最後に使っていた値
    if (m.date >= a.lastDate && m.wage > 0) {
      r.wage = m.wage;
      a.lastDate = m.date;
    }
  }
  return [...acc.values()].map((a) => {
    const r = a.row;
    r.days = a.dates.size;
    r.hours_daily = Math.round(r.hours_daily * 100) / 100;
    r.hours_raw = Math.round(r.hours_raw * 100) / 100;
    // 月まとめ: 単価ごとに(丸める前の時間 × 時給)。歩合は 取得 × 件数単価(時間は関係ない)
    if (r.pay_type === 'commission') {
      let sum = 0;
      for (const [wage, appts] of a.apptsByWage) sum += wage * appts;
      r.pay_month = Math.round(sum);
    } else {
      let sum = 0;
      for (const [wage, hours] of a.hoursByWage) sum += wage * hours;
      r.pay_month = Math.round(sum);
    }
    return r;
  });
}

/** ①の合計(日ごと・月まとめ・差) */
export function monthPayTotals(rows: MonthPayRow[]) {
  const pay_daily = rows.reduce((a, r) => a + r.pay_daily, 0);
  const pay_month = rows.reduce((a, r) => a + r.pay_month, 0);
  return {
    people: rows.length,
    hours_daily: Math.round(rows.reduce((a, r) => a + r.hours_daily, 0) * 100) / 100,
    hours_raw: Math.round(rows.reduce((a, r) => a + r.hours_raw, 0) * 100) / 100,
    appointments: rows.reduce((a, r) => a + r.appointments, 0),
    commute: rows.reduce((a, r) => a + r.commute, 0),
    pay_daily,
    pay_month,
    diff: pay_month - pay_daily,
  };
}

/** 支払いの基準(どちらの計算を正にするかは未確定 P-1。画面で選ぶ) */
export type PayBasis = 'daily' | 'month';

export function basePayOf(r: MonthPayRow, basis: PayBasis): Yen {
  return basis === 'month' ? r.pay_month : r.pay_daily;
}

/** ②歩合の1行 */
export type CommissionRow = {
  row: MonthPayRow;
  /** 入力したキャンセル件数 */
  cancels: number;
  /** キャンセル率(%) = キャンセル ÷ 取得 */
  cancel_rate: number | null;
  /** 差引後の件数 */
  net: number;
  /** 差引後の報酬 = (取得 − キャンセル) × 件数単価。マイナスにはしない */
  pay: Yen;
};

export function commissionRow(r: MonthPayRow, cancels: number): CommissionRow {
  const net = Math.max(0, r.appointments - Math.max(0, cancels));
  return {
    row: r,
    cancels: Math.max(0, cancels),
    cancel_rate: r.appointments > 0 ? (Math.max(0, cancels) / r.appointments) * 100 : null,
    net,
    pay: Math.max(0, Math.round(net * r.wage)),
  };
}

/** 支給額 = 報酬(基準で選んだ方) ＋ 交通費 ＋ インセンティブ − 前払い ＋ 調整 */
export function payoutOf(args: { base: Yen; commute: Yen; incentive: Yen; advance: Yen; adjust: Yen }): Yen {
  return Math.round(args.base + args.commute + args.incentive - args.advance + args.adjust);
}

/** その人のインセンティブ(月の取得件数の段階)。管理者は対象外 */
export function incentiveFor(r: MonthPayRow, s: IncentiveSettings): Yen {
  if (r.work_style === 'kanri') return 0;
  return incentiveOf(r.appointments, s);
}

// ---- ④ 請求書 -------------------------------------------------------------------

export type InvoiceRow = {
  company_id: string;
  name: string;
  /** 納品件数(月合計) */
  count: number;
  /** 金額 = Σ(その日の件数 × その日の単価) */
  amount: Yen;
  /** 使った単価(複数あれば複数) */
  prices: Yen[];
  /** 日ごとの明細 */
  days: { date: DateStr; count: number; price: Yen; amount: Yen }[];
};

/** ④請求書: 会社ごとに Σ(その日の納品件数 × その日の会社単価)。単価改定は日ごとに効く */
export function invoiceRows(companies: Company[], deliveries: Delivery[]): InvoiceRow[] {
  const byId = new Map(companies.map((c) => [c.id, c]));
  const acc = new Map<string, InvoiceRow>();
  for (const d of deliveries) {
    for (const [cid, detail] of Object.entries(d.details ?? {})) {
      const company = byId.get(cid);
      if (!company) continue;
      // 納品件数は by_style(出勤/歩合/時給)の合計
      const count = Object.values(detail?.by_style ?? {}).reduce((sum, x) => sum + (x ?? 0), 0);
      if (!Number.isFinite(count) || count === 0) continue;
      const price = priceOn(company, d.date);
      const amount = Math.round(count * price);
      let row = acc.get(cid);
      if (!row) {
        row = { company_id: cid, name: company.name, count: 0, amount: 0, prices: [], days: [] };
        acc.set(cid, row);
      }
      row.count += count;
      row.amount += amount;
      if (!row.prices.includes(price)) row.prices.push(price);
      row.days.push({ date: d.date, count, price, amount });
    }
  }
  const list = [...acc.values()];
  for (const r of list) {
    r.days.sort((a, b) => (a.date < b.date ? -1 : 1));
    r.prices.sort((a, b) => a - b);
  }
  return list.sort((a, b) => a.company_id.localeCompare(b.company_id));
}
